// @flow

import { isString } from 'lodash';
import { type AvailabilityEnum } from 'Components/widgets/product/Product.mapper';

type PriceArgs = {
  date?: string,
  duration?: number,
  departureCity?: string,
  forceWithoutTransport?: ?boolean,
  optimizedPriceCalls?: ?boolean,
  dateStart?: string,
  dateEnd?: string,
};

export type Args = {
  content?: string,
  release?: string,
  offerId?: string,
  offerName?: string,
  tourDurationPlaceholder?: ?string,
  cruiseTimingPlaceholder?: ?string,
  cruiseDurationPlaceholder?: ?string,
  dateFormat?: ?string,
  ...$Exact<PriceArgs>,
};

export type Options = {
  withoutPrice: boolean,
};

export type ThumbnailProductPrice = {
  bestPrice: {
    value: number,
    formattedValue: string,
  },
  initialPrice: {
    value: number,
    formattedValue: string,
  },
  unit: string,
  currency: string,
  termsAndConditions: {
    departureDate: number,
    duration: number,
    durationFormatted: string,
    numberOfAdults: number,
    departureCity: {
      value: string,
      name: string,
    },
    accommodationComfortLabel: string,
  },
};

export type ThumbnailChildcareAvailabilities = Array<{
  id: string,
  availability: AvailabilityEnum,
  ageMin: number,
  ageMax: number,
  remainingStock: number | null,
}>;

export type ThumbnailProduct = {
  +id: string,
  +isJoyview: boolean,
  +seasonTrad: string,
  +seasonName?: string,
  +isDoubleSeason: boolean,
  +badgeIcon: string,
  +comfort: number,
  +comfortLabel: string,
  +name: string,
  +openingStatus: string,
  +href: string,
  +images: Array<string>,
  +resortId: string,
  +rangeId: string,
  +rangeLabel: string,
  +duration: string,
  +timingDescription: string,
  +title: string,
  +type: string,
  +destination: {
    +country: string,
    +area: string,
  },
  +thematics: Array<{
    +id: string,
    +label: string,
  }>,
  +price?: ThumbnailProductPrice,
  +childcareAvailabilities?: ThumbnailChildcareAvailabilities,
};

type CleanedArgs = {
  content: string,
  release: string,
  cruiseTimingPlaceholder: ?string,
  cruiseDurationPlaceholder: ?string,
  date: string,
  dateFormat: ?string,
  departureCity: string,
  duration: number | string | null,
  forceWithoutTransport: boolean,
  offerId: string,
  offerName: string,
  optimizedPriceCalls: boolean,
  tourDurationPlaceholder: ?string,
  dateStart: string,
  dateEnd: string,
};

const generateCmsArgs = (content, release) => `content : "${content}" release : "${release}"`;

const getPriceArgs = (args: CleanedArgs): Array<Array<*>> => {
  return [
    'offerId',
    'offerName',
    'date',
    'dateStart',
    'dateEnd',
    'duration',
    'departureCity',
    'forceWithoutTransport',
    'optimizedPriceCalls',
  ]
    .filter(key => args[key])
    .map(key => {
      const value = isString(args[key]) ? `"${args[key]}"` : args[key];

      return [key, value];
    });
};

const buildPriceGraphqlArgs = (args: Array<Array<string>>): string =>
  args.length ? `(${args.map(priceArg => priceArg.join(' : ')).join(' ')})` : '';

export function getPriceFields(args: CleanedArgs): string {
  const { content, release } = args;
  const cmsArgs = generateCmsArgs(content, release);

  const priceArgs = buildPriceGraphqlArgs(getPriceArgs(args));

  return `
    price ${priceArgs} {
      bestPrice {
        value
        formattedValue
      }
      initialPrice {
        value
        formattedValue
      }
      unit(${cmsArgs})
      currency
      termsAndConditions {
        departureDate
        duration
        durationFormatted(${cmsArgs})
        numberOfAdults
        departureCity {
          value
          name
        }
        accommodationComfortLabel
      }
    }`;
}

function getThumbnailBaseFields(args: CleanedArgs): string {
  const {
    offerName,
    tourDurationPlaceholder,
    cruiseTimingPlaceholder,
    cruiseDurationPlaceholder,
    dateFormat,
    content,
    release,
  } = args;

  // use typeof string since we cannot be sure that there are no empty strings on the CMS
  const durationArgs =
    typeof tourDurationPlaceholder === 'string' && typeof cruiseDurationPlaceholder === 'string'
      ? `(message : "${tourDurationPlaceholder}", cruiseMessage : "${cruiseDurationPlaceholder}")`
      : '';
  const timingDescriptionArgs =
    typeof cruiseTimingPlaceholder === 'string' && typeof dateFormat === 'string'
      ? `(message : "${cruiseTimingPlaceholder}" dateFormat : "${dateFormat}")`
      : '';

  return `
    id
    isJoyview
    seasonName
    seasonTrad(${generateCmsArgs(content, release)})
    isDoubleSeason
    badgeIcon
    comfort
    comfortLabel
    name
    href(offerName : "${offerName}")
    images
    openingStatus
    resortId
    rangeId
    rangeLabel
    duration${durationArgs}
    timingDescription${timingDescriptionArgs}
    title
    type
    destination {
      country
      area
    }
    thematics {
      label
    }`;
}

export function cleanupArgs(args: Args): CleanedArgs {
  return {
    content: args.content || '',
    release: args.release || '',
    cruiseTimingPlaceholder: args.cruiseTimingPlaceholder,
    cruiseDurationPlaceholder: args.cruiseDurationPlaceholder,
    date: args.date || '',
    dateFormat: args.dateFormat,
    departureCity: args.departureCity || '',
    duration: args.duration || null,
    dateStart: args.dateStart || '',
    dateEnd: args.dateEnd || '',
    forceWithoutTransport: !!args.forceWithoutTransport,
    offerId: args.offerId || '',
    offerName: args.offerName || '',
    optimizedPriceCalls: args.optimizedPriceCalls || false,
    tourDurationPlaceholder: args.tourDurationPlaceholder,
  };
}

function makeResortThumbnailQuery(args: Args, options?: Options): string {
  const { withoutPrice = false } = options || {};
  const priceFields = !withoutPrice ? getPriceFields(cleanupArgs(args)) : '';

  return `${getThumbnailBaseFields(cleanupArgs(args))}${priceFields}`;
}

export default makeResortThumbnailQuery;
